/**
 * Signal history import (plan signal-connector.md §5.4). Two steps, because one
 * Signal backup carries EVERY chat rather than one chat per file: upload → chat
 * manifest → pick chats → import.
 *
 * Only reached with an account already linked — the wizard's import step, or
 * /sources/signal/import from the account's card. Backfilling history for a
 * Signal account nobody connected isn't a flow this UI offers.
 *
 * Two contract details shape this UI:
 *   - The manifest lists only chats with at least one indexable message, so the
 *     count shown is deliberately smaller than the backup's total chat count.
 *   - A batch's stored parse is cleared once its import completes, so step 2 can
 *     answer 410 — meaning "already imported" or "never parsed", NOT "expired
 *     from a cache". That gets its own recovery state, and the copy must not
 *     promise that nothing was imported.
 *
 * Import progress is read by polling the batch, not from the select response, so
 * this keeps working when the server moves that work to a background job.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { Check, Loader2, Lock, Search, User, Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Dropzone } from '@/components/patterns/dropzone';
import { IconTile } from '@/components/patterns/icon-tile';
import { InfoNote } from '@/components/patterns/info-note';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import {
  ApiError,
  type ImportBatch,
  importsApi,
  type SignalManifestChat,
  type SignalUploadResult,
  signalImportApi,
} from '@/lib/api';
import { qk } from '@/lib/query-client';
import { cn, formatBytes } from '@/lib/utils';

interface Props {
  onDone: () => void;
}

const POLL_INTERVAL_MS = 2_000;
const ACCEPT = '.jsonl,.json,.zip';

/** Server errors that are worth their own words rather than "upload failed". */
function uploadErrorCopy(err: unknown): string {
  if (!(err instanceof ApiError)) return 'Upload failed. Please try again.';
  switch (err.status) {
    case 413:
      return 'That file is larger than 100 MB. Signal backups this big are not supported yet.';
    case 415:
      return "That file type isn't supported — upload main.jsonl or the .zip containing it.";
    case 422:
      return "That doesn't look like a Signal export. Upload the export folder (its main.jsonl + metadata.json) as a .zip.";
    default:
      return "Brain couldn't read that backup. Please try again.";
  }
}

function dateRange(chat: SignalManifestChat): string {
  if (!chat.firstAt || !chat.lastAt) return `${chat.messageCount.toLocaleString()} messages`;
  const first = new Date(chat.firstAt).toLocaleDateString();
  const last = new Date(chat.lastAt).toLocaleDateString();
  const span = first === last ? first : `${first} – ${last}`;
  return `${chat.messageCount.toLocaleString()} messages · ${span}`;
}

export function SignalImport({ onDone }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<SignalUploadResult | null>(null);
  const [expired, setExpired] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState('');
  const [groupVisibility, setGroupVisibility] = useState<'workspace' | 'private'>('workspace');
  const [batchId, setBatchId] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: (f: File) => signalImportApi.upload(f),
    onSuccess: (res) => {
      setParsed(res);
      setExpired(false);
      // Nothing is pre-selected: importing every chat in a personal backup is
      // rarely what someone wants, and it is the expensive default.
      setSelected(new Set());
    },
    onError: (err) => toast.error(uploadErrorCopy(err)),
  });

  const runImport = useMutation({
    mutationFn: () => {
      const byKey = new Map((parsed?.manifest ?? []).map((ch) => [ch.chatKey, ch]));
      const chats = [...selected].map((chatKey) => {
        const kind = byKey.get(chatKey)?.kind;
        // Send visibility for groups only — DMs and Note-to-Self take the
        // server's private default, keeping that rule in one place.
        return kind === 'group' ? { chatKey, visibility: groupVisibility } : { chatKey };
      });
      return signalImportApi.selectChats(parsed?.batchId as string, chats);
    },
    onSuccess: (res) => setBatchId(res.batchId),
    onError: (err) => {
      if (err instanceof ApiError && err.status === 410) {
        // The batch's stored parse is gone: already imported, or never parsed.
        // Recoverable by re-uploading — say that instead of a generic failure.
        setExpired(true);
        setParsed(null);
        setBatchId(null);
        return;
      }
      toast.error('Import failed. Please try again.');
    },
  });

  // Progress comes from the batch, not the select response, so this survives the
  // import moving to a background job.
  const { data: batch } = useQuery({
    queryKey: qk.import(batchId ?? ''),
    queryFn: () => importsApi.get(batchId as string),
    enabled: !!batchId,
    refetchInterval: (q) => {
      const data = q.state.data as ImportBatch | undefined;
      if (!data) return POLL_INTERVAL_MS;
      return data.status === 'parsing' || data.status === 'indexing' ? POLL_INTERVAL_MS : false;
    },
  });

  const manifest = parsed?.manifest ?? [];
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return manifest;
    return manifest.filter((ch) => ch.title.toLowerCase().includes(q));
  }, [manifest, query]);

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((ch) => selected.has(ch.chatKey));

  function toggle(chatKey: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(chatKey)) next.delete(chatKey);
      else next.add(chatKey);
      return next;
    });
  }

  function toggleAll() {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const ch of filtered) {
        if (allFilteredSelected) next.delete(ch.chatKey);
        else next.add(ch.chatKey);
      }
      return next;
    });
  }

  function reset() {
    setFile(null);
    setParsed(null);
    setBatchId(null);
    setSelected(new Set());
    setQuery('');
  }

  const selectedMessages = useMemo(
    () =>
      manifest.filter((ch) => selected.has(ch.chatKey)).reduce((n, ch) => n + ch.messageCount, 0),
    [manifest, selected],
  );

  // ---- Import in flight / finished -----------------------------------------

  if (batchId) {
    // Counts live only on the batch row — step 2 returns none (202 + a job).
    const counts = batch?.counts ?? null;
    const collections = runImport.data?.collections ?? [];
    const done = batch?.status === 'done';
    const failed = batch?.status === 'failed';
    const pct =
      counts && counts.parsed > 0 ? Math.round((counts.imported / counts.parsed) * 100) : 0;

    return (
      <div className="space-y-6">
        <div className="rounded-xl border border-border p-8">
          <IconTile tone={done ? 'green' : 'neutral'}>
            {done ? <Check className="h-5 w-5" /> : <Loader2 className="h-5 w-5 animate-spin" />}
          </IconTile>
          <h2 className="mt-4 font-semibold text-lg">
            {failed ? 'Import failed' : done ? 'History imported' : 'Importing your history…'}
          </h2>
          <p className="mt-1.5 text-[14px] text-muted-foreground">
            {failed
              ? (batch?.error ?? 'Something went wrong part-way through the import.')
              : done
                ? 'Your Signal history is searchable now.'
                : 'This keeps running in the background — you can continue setup and check progress on Sources.'}
          </p>

          {counts ? (
            <div className="mt-6 space-y-2">
              <Progress value={pct} />
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[13px] text-muted-foreground">
                <span>Imported: {counts.imported.toLocaleString()}</span>
                <span>Duplicates skipped: {counts.deduped.toLocaleString()}</span>
                <span>Parsed: {counts.parsed.toLocaleString()}</span>
                {counts.media_transcribed ? (
                  <span>Voice notes transcribed: {counts.media_transcribed.toLocaleString()}</span>
                ) : null}
                {counts.media_skipped ? (
                  <span>Other media referenced: {counts.media_skipped.toLocaleString()}</span>
                ) : null}
              </div>
              {counts.system_skipped > 0 && (
                // Real backups carry hundreds of these, so "imported" lands well
                // below the manifest count. Explain it before someone reports it.
                <p className="text-[12.5px] text-faint">
                  {counts.system_skipped.toLocaleString()} non-message items skipped — group
                  updates, deleted messages, polls and view-once media.
                </p>
              )}
            </div>
          ) : (
            // Counts only land when the job finishes, so show the collections the
            // server created synchronously rather than an empty progress bar.
            collections.length > 0 && (
              <ul className="mt-6 space-y-1.5">
                {collections.map((col) => (
                  <li key={col.collectionId} className="flex items-center gap-2 text-[13px]">
                    {col.kind === 'group' ? (
                      <Users className="h-3.5 w-3.5 shrink-0 text-faint" />
                    ) : (
                      <User className="h-3.5 w-3.5 shrink-0 text-faint" />
                    )}
                    <span className="min-w-0 flex-1 truncate">{col.title}</span>
                    {col.visibility === 'private' && <Lock className="h-3 w-3 text-faint" />}
                  </li>
                ))}
              </ul>
            )
          )}

          {!!(counts?.media_transcribed || counts?.media_skipped) && (
            <InfoNote className="mt-4">
              Text and transcribed voice notes are searchable. Other attachments — images and files
              — are noted but their contents aren&apos;t imported yet.
            </InfoNote>
          )}

          {done && (
            // Collections are keyed on the user, not the backup file — worth
            // saying, because duplicate-import pain elsewhere makes people wary
            // of ever re-importing.
            <InfoNote className="mt-4">
              Importing a newer backup later adds only the new messages — it won&apos;t duplicate
              what&apos;s already here.
            </InfoNote>
          )}
        </div>

        <Button size="cta" className="w-full" onClick={onDone}>
          {done ? 'Continue' : 'Continue — import keeps running'}
        </Button>
      </div>
    );
  }

  // ---- Chat picker ---------------------------------------------------------

  if (parsed) {
    return (
      <div className="space-y-5">
        <div>
          <h2 className="font-semibold text-lg">Choose chats to import</h2>
          <p className="mt-1.5 text-[14px] text-muted-foreground">
            {/* summary.chats counts every chat; the manifest lists only the ones
                that actually contain messages worth importing. */}
            {manifest.length.toLocaleString()} chats with messages, out of{' '}
            {parsed.summary.chats.toLocaleString()} total in this backup.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border-subtle p-3 text-[13px]">
          <span className="text-muted-foreground">Imported groups are</span>
          <div className="inline-flex overflow-hidden rounded-md border border-border">
            {(['workspace', 'private'] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setGroupVisibility(v)}
                className={cn(
                  'px-3 py-1 text-[12.5px] transition-colors',
                  groupVisibility === v
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {v === 'workspace' ? 'Shared with workspace' : 'Private to me'}
              </button>
            ))}
          </div>
          <span className="ml-auto inline-flex items-center gap-1 text-faint">
            <Lock className="h-3 w-3" /> DMs are always private to you
          </span>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="-translate-y-1/2 absolute top-1/2 left-3 h-4 w-4 text-faint" />
            <Input
              placeholder="Search chats…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button variant="outline" size="sm" onClick={toggleAll} disabled={filtered.length === 0}>
            {allFilteredSelected ? 'Clear' : 'Select all'}
          </Button>
        </div>

        <div className="max-h-[360px] overflow-y-auto rounded-xl border border-border">
          {filtered.length === 0 ? (
            <p className="p-6 text-center text-[13px] text-faint">No chats match your search.</p>
          ) : (
            filtered.map((chat) => {
              const isOn = selected.has(chat.chatKey);
              return (
                <button
                  key={chat.chatKey}
                  type="button"
                  aria-pressed={isOn}
                  onClick={() => toggle(chat.chatKey)}
                  className="flex w-full items-center gap-3 border-border-subtle border-b px-4 py-3 text-left last:border-b-0 hover:bg-muted/40"
                >
                  <span
                    className={cn(
                      'flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors',
                      isOn ? 'border-primary bg-primary text-primary-foreground' : 'border-border',
                    )}
                  >
                    {isOn && <Check className="h-3.5 w-3.5" />}
                  </span>
                  {chat.kind === 'group' ? (
                    <Users className="h-4 w-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <User className="h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[14px]">{chat.title}</span>
                    <span className="block truncate text-[12.5px] text-faint">
                      {dateRange(chat)}
                    </span>
                  </span>
                  {chat.kind !== 'group' && (
                    <span className="inline-flex shrink-0 items-center gap-1 text-[11.5px] text-faint">
                      <Lock className="h-3 w-3" /> Private
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>

        <div className="flex items-center gap-3">
          <span className="text-[13px] text-faint">
            {selected.size} selected · {selectedMessages.toLocaleString()} messages
          </span>
          <Button
            size="cta"
            className="ml-auto"
            disabled={selected.size === 0 || runImport.isPending}
            onClick={() => runImport.mutate()}
          >
            {runImport.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Starting…
              </>
            ) : (
              `Import ${selected.size} chat${selected.size === 1 ? '' : 's'}`
            )}
          </Button>
        </div>

        <button
          type="button"
          onClick={reset}
          className="text-[13px] text-faint transition-colors hover:text-foreground"
        >
          ← Use a different backup
        </button>
      </div>
    );
  }

  // ---- Upload --------------------------------------------------------------

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border p-8">
        <IconTile tone="neutral">
          <Users className="h-5 w-5" />
        </IconTile>
        <h2 className="mt-4 font-semibold text-lg">Import your Signal history</h2>
        <p className="mt-1.5 text-[14px] text-muted-foreground">
          Export your Signal history, then zip the folder — its{' '}
          <span className="font-medium">main.jsonl</span> and{' '}
          <span className="font-medium">metadata.json</span> — and upload it here. Zipping the
          folder brings voice notes across too. Then pick which conversations to make searchable.
        </p>

        {expired && (
          <Alert variant="warning" className="mt-6">
            <AlertTitle>This backup can&apos;t be imported again from here</AlertTitle>
            <AlertDescription>
              {/* The server clears a batch's stored parse once its import finishes,
                  so a 410 means "already imported" OR "never parsed" — we can't
                  tell which, and must not claim nothing was imported. */}
              It has either already been imported, or it couldn&apos;t be read. Check Sources first;
              if your chats aren&apos;t there, upload the file again — importing the same backup
              twice won&apos;t create duplicates.
            </AlertDescription>
          </Alert>
        )}

        <div className="mt-6">
          {upload.isPending ? (
            <div className="flex items-center gap-3 rounded-lg border border-border p-4">
              <Loader2 className="h-5 w-5 shrink-0 animate-spin text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-sm">Reading {file?.name}…</p>
                <p className="text-[13px] text-faint">
                  Large backups take a moment — this happens once.
                </p>
              </div>
            </div>
          ) : (
            <Dropzone
              accept={ACCEPT}
              maxLabel="up to 100 MB"
              onFile={(f) => {
                setFile(f);
                setExpired(false);
                upload.mutate(f);
              }}
            />
          )}
        </div>

        {file && !upload.isPending && (
          <p className="mt-3 text-[13px] text-faint">
            {file.name} · {formatBytes(file.size)}
          </p>
        )}

        <InfoNote className="mt-4">
          Text and transcribed voice notes are searchable. Other attachments — images and files —
          are noted but their contents aren&apos;t imported yet.
        </InfoNote>
      </div>
    </div>
  );
}
